// Tiny localStorage persistence helper for Redux slices, replacing the
// zustand `persist` middleware without adding a dependency (redux-persist).
// Slices read their initial state via loadPersisted(); store.ts writes the
// persisted slices back on change via savePersisted().
export function loadPersisted<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    // Guard against corrupt/legacy values (string, array, null, ...) so callers
    // always get a well-shaped slice rather than a broken merge.
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return fallback;
    }
    return { ...fallback, ...parsed };
  } catch {
    return fallback;
  }
}

// One-time migration: read state persisted by the former zustand `persist`
// middleware, which wraps the slice state as `{ state: {...}, version: n }`.
// Used as the fallback for loadPersisted so existing users keep their data on
// upgrade until the new key is written.
export function readLegacyZustand<T>(legacyKey: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(legacyKey);
    if (!raw) return fallback;
    const state = JSON.parse(raw)?.state;
    if (state === null || typeof state !== 'object' || Array.isArray(state)) {
      return fallback;
    }
    return { ...fallback, ...state };
  } catch {
    return fallback;
  }
}

export function savePersisted(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable or over quota — non-fatal
  }
}
